import { FileText, PenTool, Sparkles, Zap, Shield, Clock, ArrowRight, Play, Star, Users, Award } from 'lucide-react'

interface DashboardProps {
  onNavigate: (page: 'lesson-plan' | 'grading') => void
}

function Dashboard({ onNavigate }: DashboardProps) {
  return (
    <div className="min-h-screen">
      {/* Animated background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl animate-float"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl animate-float" style={{animationDelay: '1s'}}></div>
        <div className="absolute top-1/2 left-1/2 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl animate-float" style={{animationDelay: '2s'}}></div>
      </div>

      {/* Header */}
      <header className="relative z-10 w-full px-6 py-4 flex justify-between items-center backdrop-blur-md bg-white/5 border-b border-white/10">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-purple-600 rounded-xl flex items-center justify-center shadow-lg animate-pulse-glow">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <span className="text-xl font-bold bg-gradient-to-r from-white to-blue-200 bg-clip-text text-transparent">EduSpark</span>
        </div>
        <div className="flex items-center gap-4">
          <a href="#features" className="hidden md:block text-white/70 hover:text-white transition-colors">Features</a>
          <a href="#standards" className="hidden md:block text-white/70 hover:text-white transition-colors">Standards</a>
          <button className="px-5 py-2.5 text-sm font-medium text-white bg-white/10 hover:bg-white/20 border border-white/20 rounded-full transition-all">
            Sign In
          </button>
        </div>
      </header>

      {/* Hero Section */}
      <main className="relative z-10">
        <section className="px-4 py-20 md:py-32 text-center">
          {/* Trust Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 text-blue-300 text-sm mb-8 backdrop-blur-sm">
            <Star className="w-4 h-4 text-yellow-400" />
            <span>Aligned with Common Core & NGSS Standards</span>
          </div>

          {/* Main Title */}
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
            Lesson Plans
            <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent"> in Minutes</span>
          </h1>
          
          <p className="text-xl md:text-2xl text-white/60 max-w-3xl mx-auto mb-12">
            Stop spending hours on lesson planning. EduSpark creates standards-aligned, 
            differentiated lesson plans for K-12 teachers in under 2 minutes.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
            <button 
              onClick={() => onNavigate('lesson-plan')}
              className="group px-8 py-4 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-semibold rounded-full hover:shadow-2xl hover:shadow-blue-500/30 transition-all flex items-center justify-center gap-2 text-lg"
            >
              <Play className="w-5 h-5" />
              Create Free Lesson Plan
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
            <button 
              onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })}
              className="px-8 py-4 text-white/80 font-semibold rounded-full border border-white/20 hover:bg-white/10 transition-all flex items-center gap-2"
            >
              <Zap className="w-5 h-5" />
              See How It Works
            </button>
          </div>

          {/* Social Proof */}
          <div className="flex items-center justify-center gap-8 text-white/50 text-sm">
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              <span>K-12 Teachers</span>
            </div>
            <div className="flex items-center gap-2">
              <Award className="w-4 h-4" />
              <span>Common Core Aligned</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              <span>Save 5+ Hours/Week</span>
            </div>
          </div>
        </section>

        {/* Feature Cards */}
        <section id="features" className="px-4 py-20">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-4">
              Built for U.S. K-12 Teachers
            </h2>
            <p className="text-white/50 text-center mb-16 max-w-2xl mx-auto">
              Every lesson plan includes Common Core State Standards, differentiation strategies, and formative assessments
            </p>

            <div className="grid md:grid-cols-2 gap-8">
              {/* Lesson Plan Card */}
              <button
                onClick={() => onNavigate('lesson-plan')}
                className="group relative bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-md rounded-3xl p-10 border border-white/10 hover:border-blue-400/50 transition-all duration-500 text-left overflow-hidden hover:shadow-2xl hover:shadow-blue-500/20"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/20 to-purple-500/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                <div className="relative">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-400 to-blue-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg shadow-blue-500/30 group-hover:scale-110 transition-transform duration-300">
                    <FileText className="w-8 h-8 text-white" />
                  </div>
                  <h2 className="text-2xl font-bold text-white mb-3">AI Lesson Plans</h2>
                  <p className="text-white/60 mb-6 text-lg">
                    Generate complete lesson plans with learning objectives, activities, and assessments in under 2 minutes.
                  </p>
                  <ul className="space-y-2 mb-6 text-white/70">
                    <li className="flex items-center gap-2">
                      <CheckIcon /> Common Core & NGSS aligned
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckIcon /> Built-in differentiation strategies
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckIcon /> Formative assessments included
                    </li>
                  </ul>
                  <div className="flex items-center text-blue-400 font-semibold group-hover:gap-2 transition-all">
                    Try It Free
                    <ArrowRight className="w-5 h-5 ml-1 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </button>

              {/* Grading Card */}
              <button
                onClick={() => onNavigate('grading')}
                className="group relative bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-md rounded-3xl p-10 border border-white/10 hover:border-green-400/50 transition-all duration-500 text-left overflow-hidden hover:shadow-2xl hover:shadow-green-500/20"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-green-500/20 to-emerald-500/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                <div className="relative">
                  <div className="w-16 h-16 bg-gradient-to-br from-green-400 to-emerald-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg shadow-green-500/30 group-hover:scale-110 transition-transform duration-300">
                    <PenTool className="w-8 h-8 text-white" />
                  </div>
                  <h2 className="text-2xl font-bold text-white mb-3">Smart Grading Assistant</h2>
                  <p className="text-white/60 mb-6 text-lg">
                    Get AI-powered feedback on student work with detailed suggestions for improvement.
                  </p>
                  <ul className="space-y-2 mb-6 text-white/70">
                    <li className="flex items-center gap-2">
                      <CheckIcon /> Rubric-based scoring
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckIcon /> Personalized feedback comments
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckIcon /> Growth-focused suggestions
                    </li>
                  </ul>
                  <div className="flex items-center text-green-400 font-semibold group-hover:gap-2 transition-all">
                    Try It Free
                    <ArrowRight className="w-5 h-5 ml-1 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </button>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="px-4 py-16 border-t border-white/10">
          <div className="max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8">
            <Stat number="300K+" label="U.S. Teachers" />
            <Stat number="50+" label="States" />
            <Stat number="2 min" label="Per Lesson Plan" />
            <Stat number="5+ hrs" label="Saved Weekly" />
          </div>
        </section>

        {/* Standards Section */}
        <section id="standards" className="px-4 py-20">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-4">
              Standards Aligned
            </h2>
            <p className="text-white/50 text-center mb-16 max-w-2xl mx-auto">
              Every lesson plan automatically includes relevant standards
            </p>
            
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 text-center">
                <div className="text-4xl mb-4">📚</div>
                <h3 className="text-xl font-semibold text-white mb-2">Common Core</h3>
                <p className="text-white/60">Math & ELA Standards for K-12</p>
              </div>
              <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 text-center">
                <div className="text-4xl mb-4">🔬</div>
                <h3 className="text-xl font-semibold text-white mb-2">NGSS</h3>
                <p className="text-white/60">Next Generation Science Standards</p>
              </div>
              <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 text-center">
                <div className="text-4xl mb-4">🎯</div>
                <h3 className="text-xl font-semibold text-white mb-2">State Standards</h3>
                <p className="text-white/60">TEKS, California, New York & more</p>
              </div>
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="px-4 py-20">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-4">
              Why U.S. Teachers Love EduSpark
            </h2>
            <p className="text-white/50 text-center mb-16 max-w-2xl mx-auto">
              Designed for American classrooms, by educators who understand your needs
            </p>
            
            <div className="grid md:grid-cols-3 gap-8">
              <FeatureCard 
                icon={<Zap className="w-6 h-6 text-blue-400" />}
                title="Save 5+ Hours Weekly"
                description="Stop spending Sunday nights on lesson planning. Generate complete, standards-aligned plans in 2 minutes."
              />
              <FeatureCard 
                icon={<Shield className="w-6 h-6 text-purple-400" />}
                title="Standards Guaranteed"
                description="Every lesson includes relevant Common Core, NGSS, or state standards. Admin-ready documentation."
              />
              <FeatureCard 
                icon={<Clock className="w-6 h-6 text-green-400" />}
                title="Differentiation Built-In"
                description="Automatic strategies for IEPs, ELLs, gifted learners, and struggling students."
              />
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="px-4 py-20">
          <div className="max-w-4xl mx-auto text-center bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-3xl p-12 border border-white/10">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Ready to Reclaim Your Weekends?
            </h2>
            <p className="text-white/60 mb-8 text-lg">
              Join thousands of U.S. teachers who are saving 5+ hours every week with EduSpark.
            </p>
            <button 
              onClick={() => onNavigate('lesson-plan')}
              className="px-8 py-4 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-semibold rounded-full hover:shadow-2xl hover:shadow-blue-500/30 transition-all inline-flex items-center gap-2"
            >
              <Sparkles className="w-5 h-5" />
              Create Your First Lesson Plan
            </button>
          </div>
        </section>

        {/* Footer */}
        <footer className="px-4 py-12 border-t border-white/10">
          <div className="max-w-6xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-gradient-to-br from-blue-400 to-purple-600 rounded-lg flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-white" />
              </div>
              <span className="text-lg font-bold text-white">EduSpark</span>
            </div>
            <p className="text-white/40 text-sm">© 2025 EduSpark. All rights reserved.</p>
            <div className="flex gap-6">
              <a href="#" className="text-white/40 hover:text-white text-sm transition-colors">Privacy</a>
              <a href="#" className="text-white/40 hover:text-white text-sm transition-colors">Terms</a>
              <a href="#" className="text-white/40 hover:text-white text-sm transition-colors">Contact</a>
            </div>
          </div>
        </footer>
      </main>
    </div>
  )
}

// Helper components
function CheckIcon() {
  return (
    <svg className="w-5 h-5 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  )
}

function Stat({ number, label }: { number: string; label: string }) {
  return (
    <div className="text-center">
      <div className="text-4xl font-bold text-white mb-2">{number}</div>
      <div className="text-white/50">{label}</div>
    </div>
  )
}

function FeatureCard({ icon, title, description }: { icon: React.ReactNode; title: string; description: string }) {
  return (
    <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
      <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center mb-4">
        {icon}
      </div>
      <h3 className="text-xl font-semibold text-white mb-2">{title}</h3>
      <p className="text-white/60">{description}</p>
    </div>
  )
}

export default Dashboard